import {
  type GetPaginationSessionsParams,
  type SessionsPresenter,
  useInfiniteScrollFromCache,
  useLazyGetSessionsPresentersQuery,
} from '@carboncopyco/data';
import { Modal, TextSubHeader } from '@carboncopyco/ui';
import {
  getDateFromDaysAgo,
  getNowInFormat,
  getTimezoneName,
} from '@carboncopyco/utils';
import { useEffect, useMemo } from 'react';
import { FixedTable } from '../../../../FixedTable';
import { TableRowWrapper } from '../../../../InfoPage';
import { useDashboardProps } from '../../../DashboardContext';
import { MinimalTableSkeleton } from '../../../MinimalTable/MinimalTableSkeleton';
import { getCols } from './presentersCols';

const cols = getCols(true);

export const ViewAllPresentersModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { organizationId, selectedWorkspaceIds, timeRange, short, internal } =
    useDashboardProps();

  const fetchArgs = useMemo<GetPaginationSessionsParams>(
    () => ({
      organizationId,
      shortSessions: short,
      internalSessions: internal,
      workspaceIds: selectedWorkspaceIds,
      start: getDateFromDaysAgo(timeRange),
      end: getNowInFormat('YYYY-MM-DDTHH:mm:ssZ'),
      timezone: getTimezoneName(),
      resourceTypes: ['managed_presentation', 'play'],
    }),
    [organizationId, short, internal, selectedWorkspaceIds, timeRange]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    totalSize,
  } = useInfiniteScrollFromCache<
    SessionsPresenter,
    GetPaginationSessionsParams
  >(useLazyGetSessionsPresentersQuery, fetchArgs);

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs]);

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 pb-7">
          <TextSubHeader
            color="text-ds-natural-600"
            label="Presenters"
          />
          {!!totalSize && (
            <TextSubHeader
              label={`(${totalSize})`}
              color="text-ds-natural-300"
            />
          )}
        </div>
      }
      open={open}
      onClose={onClose}
    >
      <div className="p-1">
        <FixedTable
          cols={cols}
          data={items}
          isLoading={isLoading}
          loader={<MinimalTableSkeleton rows={11} />}
          isLoadingBatch={isLoadingBatch}
          handleFetchNextBatch={fetchNextBatch}
          error={error}
          rowWrapper={TableRowWrapper}
          infiniteListMaxHeight={600}
        />
      </div>
    </Modal>
  );
};
